// src/weather/wxSeason.js — the rules behind the Weather page's Season tab.
//
// Kevin, 2026-11-01, on the weather redesign: "Yes fix everything. Make weather data
// perfect". The Season tab answers one question for each county a farm insures:
// how did this season's rain and heat run against normal, and where do the crop's
// own RMA planting dates fall on it.
//
// Pure: no fetch, no React. The page hands in the rows it read; tools/check-wx-season.js
// drives these same functions offline, so the page and the checker cannot disagree.
//
// ⚠ ONE INSTRUMENT ON BOTH SIDES. The normal in wny_grid_climatology is built from
// gridMET, so the season is read from gridMET too (gridmetPrecipMm, the same rule
// Weather Records uses). The radar value that the sync writes into precip_mm on some
// days is never summed against that normal.
//
// ⚠ A MISSING READING IS A GAP, NEVER ZERO. A county day counts only when at least
// half its cells reported gridMET that day. A day that doesn't count adds nothing to
// the season AND nothing to the normal, so the two running totals always cover the
// same days, and the gap is reported.
//
// ⚠ A COUNTY DAY IS THE MEAN OF ITS CELLS, AND ITS NORMAL IS THE MEAN OF THE SAME
// CELLS. Averaging the normal over every cell while the day averaged only the cells
// that reported would compare two different areas.

const WX = require("./wxEvidence");

const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const r2 = (n) => (n == null ? null : Math.round(n * 100) / 100);
const r1 = (n) => (n == null ? null : Math.round(n * 10) / 10);
const mmToIn = (mm) => (mm == null ? null : mm / 25.4);
const cToF = (c) => (c == null ? null : (c * 9) / 5 + 32);

// Share of a county's cells that must report gridMET for the day to count.
const MIN_CELL_SHARE = 0.5;
// The page's season: April 1 through November 30 of the crop year.
const SEASON_START_MD = "04-01";
const SEASON_END_MD = "11-30";
const HARD_FREEZE_F = 28;
const GDD_BASE_F = 50;
const GDD_CAP_F = 86;

// The climatology folds Feb 29 into the calendar (day 60 onward shifts by one in a
// leap year) and stores days 1..365, so a leap year's Dec 31 (day 366) reads day 365.
function climoDoy(iso) {
  const d = WX.dayOfYear(iso);
  return d == null ? null : Math.min(365, d);
}

function isoAdd(iso, days) {
  const t = Date.parse(iso + "T00:00:00Z");
  return new Date(t + days * 86400000).toISOString().slice(0, 10);
}

// Daily GDD the way the stored normal was built: high capped at 86°F, low floored at
// 50°F and capped at 86°F, averaged, minus 50. Computed per cell, then averaged across
// cells, which is how the normal was averaged across years.
function gddF(tmaxC, tminC) {
  const hi = cToF(num(tmaxC)), lo = cToF(num(tminC));
  if (hi == null || lo == null) return null;
  const tx = Math.min(hi, GDD_CAP_F);
  const tn = Math.max(Math.min(lo, GDD_CAP_F), GDD_BASE_F);
  return Math.max(0, (tx + tn) / 2 - GDD_BASE_F);
}

function seasonBounds(year, latestIso) {
  const y = Number(year);
  const from = `${y}-${SEASON_START_MD}`;
  let to = `${y}-${SEASON_END_MD}`;
  if (latestIso && latestIso < to) to = latestIso;
  return { from, to: to < from ? null : to };
}

// rows: wny_grid_weather_daily for the county's cells (observed rows; forecasts are
// ignored here). norms: wny_grid_climatology rows for the same cells. cellIds: every
// cell the county has, so the share that reported can be judged.
function countyDaily({ cellIds = [], rows = [], norms = [], from, to }) {
  const cells = [...new Set(cellIds.map(String))];
  const need = Math.max(1, Math.ceil(cells.length * MIN_CELL_SHARE));
  const byDate = new Map();
  for (const r of rows) {
    if (!r || r.is_forecast === true) continue;
    const d = String(r.obs_date || "").slice(0, 10);
    if (!d || d < from || d > to) continue;
    if (!byDate.has(d)) byDate.set(d, new Map());
    byDate.get(d).set(String(r.cell_id), r);
  }
  const normBy = new Map();
  for (const n of norms) normBy.set(`${n.cell_id}|${n.day_of_year}`, n);

  const out = [];
  let runRain = 0, runNorm = 0, runGdd = 0, runGddNorm = 0;
  for (let d = from; d <= to; d = isoAdd(d, 1)) {
    const doy = climoDoy(d);
    const day = byDate.get(d) || new Map();
    let rainSum = 0, normSum = 0, rainN = 0, normMissing = 0;
    let gSum = 0, gNormSum = 0, gN = 0, txSum = 0, tnSum = 0, tN = 0;
    for (const c of cells) {
      const r = day.get(c);
      const n = normBy.get(`${c}|${doy}`);
      const gm = r ? WX.gridmetPrecipMm(r) : null;
      if (gm != null) {
        const np = n ? num(n.precip_mm) : null;
        if (np == null) normMissing++;
        else { rainSum += gm; normSum += np; rainN++; }
      }
      const g = r ? gddF(r.tmax_c, r.tmin_c) : null;
      const gn = n ? num(n.gdd_f) : null;
      if (g != null && gn != null) { gSum += g; gNormSum += gn; gN++; }
      const tx = r ? num(r.tmax_c) : null, tn = r ? num(r.tmin_c) : null;
      if (tx != null && tn != null) { txSum += tx; tnSum += tn; tN++; }
    }
    const rainOk = rainN >= need;
    const gddOk = gN >= need;
    const rain_in = rainOk ? mmToIn(rainSum / rainN) : null;
    const normal_in = rainOk ? mmToIn(normSum / rainN) : null;
    if (rainOk) { runRain += rain_in; runNorm += normal_in; }
    const gdd = gddOk ? gSum / gN : null;
    const normal_gdd = gddOk ? gNormSum / gN : null;
    if (gddOk) { runGdd += gdd; runGddNorm += normal_gdd; }
    out.push({
      date: d,
      rain_in: r2(rain_in), normal_in: r2(normal_in),
      rain_cum_in: r2(runRain), normal_cum_in: r2(runNorm),
      cells_reporting: rainN, cells_total: cells.length, norm_missing: normMissing,
      counted: rainOk,
      tmax_f: tN >= need ? r1(cToF(txSum / tN)) : null,
      tmin_f: tN >= need ? r1(cToF(tnSum / tN)) : null,
      gdd: r1(gdd), normal_gdd: r1(normal_gdd),
      gdd_cum: r1(runGdd), normal_gdd_cum: r1(runGddNorm),
    });
  }
  return out;
}

// gridMET lags a day or two, so the newest days of an open season haven't been
// published yet. Those trailing days are not gaps; they are dropped so the season
// ends on the last day that counted.
function trimTail(days) {
  let end = days.length;
  while (end > 0 && !days[end - 1].counted) end--;
  return days.slice(0, end);
}

// Rain and normal over the counted days between two dates (inclusive), with the
// days that didn't count named, so a window total never hides a gap.
function windowTotal(days, from, to) {
  let rain = 0, norm = 0, counted = 0, missing = 0;
  const gaps = [];
  for (const d of days) {
    if (d.date < from || d.date > to) continue;
    if (d.counted) { rain += d.rain_in; norm += d.normal_in; counted++; }
    else { missing++; gaps.push(d.date); }
  }
  if (!counted) return { rain_in: null, normal_in: null, pct: null, departure_in: null, counted, missing, gaps };
  return {
    rain_in: r2(rain), normal_in: r2(norm),
    pct: norm > 0 ? Math.round((rain / norm) * 100) : null,
    departure_in: r2(rain - norm), counted, missing, gaps,
  };
}

function monthTotals(days) {
  const by = new Map();
  for (const d of days) {
    const m = d.date.slice(0, 7);
    if (!by.has(m)) by.set(m, { month: m, first: d.date, last: d.date });
    by.get(m).last = d.date;
  }
  return [...by.values()].map((m) => {
    const t = windowTotal(days, m.first, m.last);
    const daysIn = new Date(Date.UTC(+m.month.slice(0, 4), +m.month.slice(5, 7), 0)).getUTCDate();
    const coveredTo = Number(m.last.slice(8, 10));
    // A month the range starts inside is as partial as one it ends inside.
    return { month: m.month, ...t, partial: coveredTo < daysIn || Number(m.first.slice(8, 10)) > 1 };
  });
}

// The first day from August 1 on when the county's average low reached the hard
// freeze line. Spring frosts are not a season-ending freeze, so they are not this.
function firstHardFreeze(days, thresholdF = HARD_FREEZE_F) {
  for (const d of days) {
    if (d.date.slice(5) < "08-01") continue;
    if (d.tmin_f != null && d.tmin_f <= thresholdF) return { date: d.date, tmin_f: d.tmin_f };
  }
  return null;
}

// The median first-28°F day across the county's cells (wny_grid_cells.frost28_doy_med),
// as a date in the given year. Null when fewer than half the cells carry one.
function medianFreezeDate(cells, year) {
  const v = (cells || []).map((c) => num(c && c.frost28_doy_med)).filter((x) => x != null).sort((a, b) => a - b);
  if (!v.length || v.length < Math.ceil((cells || []).length * MIN_CELL_SHARE)) return null;
  const mid = v.length % 2 ? v[(v.length - 1) / 2] : Math.round((v[v.length / 2 - 1] + v[v.length / 2]) / 2);
  return isoAdd(`${year}-01-01`, mid - 1);
}

// A custom range for the Season view, held inside one calendar year (the normals
// are read by day of year) and never past today. From after To is swapped. Null when
// either end is not a date.
function clampRange(from, to, year, todayIso) {
  const ok = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
  if (!ok(from) || !ok(to)) return null;
  const y = Number(year);
  const lo = `${y}-01-01`;
  let hi = `${y}-12-31`;
  if (ok(todayIso) && todayIso < hi) hi = todayIso;
  if (hi < lo) return null;
  let a = String(from), b = String(to);
  if (a > b) [a, b] = [b, a];
  if (a < lo) a = lo;
  if (b > hi) b = hi;
  if (a > hi) a = hi;
  if (b < lo) b = lo;
  return { from: a, to: b };
}

// The same month and day a year earlier; Feb 29 becomes Feb 28.
function priorYear(iso) {
  const y = Number(iso.slice(0, 4)) - 1, md = iso.slice(5);
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  return `${y}-${md === "02-29" && !leap ? "02-28" : md}`;
}

function seasonSummary(days) {
  const counted = days.filter((d) => d.counted);
  const last = counted.length ? counted[counted.length - 1] : null;
  const gddDays = days.filter((d) => d.gdd != null);
  const lastG = gddDays.length ? gddDays[gddDays.length - 1] : null;
  return {
    from: days.length ? days[0].date : null,
    to: last ? last.date : null,
    rain_in: last ? last.rain_cum_in : null,
    normal_in: last ? last.normal_cum_in : null,
    pct: last && last.normal_cum_in > 0 ? Math.round((last.rain_cum_in / last.normal_cum_in) * 100) : null,
    departure_in: last ? r2(last.rain_cum_in - last.normal_cum_in) : null,
    gdd: lastG ? Math.round(lastG.gdd_cum) : null,
    normal_gdd: lastG ? Math.round(lastG.normal_gdd_cum) : null,
    gdd_pct: lastG && lastG.normal_gdd_cum > 0 ? Math.round((lastG.gdd_cum / lastG.normal_gdd_cum - 1) * 100) : null,
    counted: counted.length,
    missing: days.length - counted.length,
    gaps: days.filter((d) => !d.counted).map((d) => d.date),
  };
}

// RMA planting dates for each spring crop a farm insures in one county, resolved with
// src/rmaPlantingWindow.js on base plans only. fields: the farm's policy_fields rows
// in that county ({rma_crop_code, crop, type_code, acres}). rmaRows: rma_dates rows for
// that state, county and crop year. A crop whose types carry different windows gets
// one entry per window, named by type; a crop RMA gives no window is left out with a
// reason. Fall-seeded crops are listed apart, because their dates were last autumn.
function plantingDatesFor(fields, rmaRows, resolveWindow, isFallSeededCrop) {
  const crops = new Map();
  for (const f of fields || []) {
    const code = String(f.rma_crop_code || "");
    if (!code || code === "0088") continue;              // PRF has no planting dates
    if (!crops.has(code)) crops.set(code, { code, crop: f.crop, types: new Map(), acres: 0 });
    const c = crops.get(code);
    const ac = num(f.acres) || 0;
    c.acres += ac;
    const t = f.type_code == null ? "" : String(f.type_code);
    c.types.set(t, (c.types.get(t) || 0) + ac);
  }
  const spring = [], fall = [], unresolved = [];
  for (const c of [...crops.values()].sort((a, b) => b.acres - a.acres)) {
    const rows = (rmaRows || []).filter((r) => String(r.commodity_code) === c.code);
    const windows = new Map();
    const notes = [];
    for (const [t, ac] of c.types) {
      const res = resolveWindow(rows, { typeCode: t || null, basePlansOnly: true });
      if (!res.window) { notes.push(res.reason); continue; }
      const w = res.window;
      const k = [w.earliest_planting_date, w.final_planting_date, w.end_of_late_planting_date].join("|");
      if (!windows.has(k)) windows.set(k, { ...w, types: [], acres: 0 });
      windows.get(k).types.push(t);
      windows.get(k).acres += ac;
    }
    const list = [...windows.values()].sort((a, b) => b.acres - a.acres);
    const entry = { code: c.code, crop: c.crop, acres: Math.round(c.acres), windows: list, split: list.length > 1 };
    if (!list.length) { unresolved.push({ ...entry, reason: notes[0] || "no window published" }); continue; }
    if (isFallSeededCrop && isFallSeededCrop(c.crop)) fall.push(entry);
    else spring.push(entry);
  }
  return { spring, fall, unresolved };
}

// The farm's own planting in one county: each insured spring field's planting date
// with its acres, by crop. The acreage report's date comes first, because it is the
// date the field is insured on. John Deere's first planting pass on that CLU this
// season fills a field the report left blank. A date outside Mar 1 to Jul 31 of the
// crop year is not a spring planting and is dropped. A field with neither date stays
// undated: it is never given RMA's earliest planting date, which is when planting may
// begin, not when it did. fields: policy_fields rows ({rma_crop_code, crop, clu_id,
// report_date, acres}). jdFirst: Map of jdKey(clu_id, crop) -> first JD planting date
// of that crop on that CLU (the CLU lower-cased: the two tables disagree on case), or null.
// One name per crop for matching a John Deere pass to an insured field: JD writes
// "CORN SILAGE" or "SOYBEANS" where the policy says "CORN" or "SOYBEANS". A pass for
// another crop on the same CLU (a split field) is never read as this crop's planting.
function plantFamily(name) {
  const s = String(name || "").toUpperCase().replace(/[_\s]+/g, " ").trim();
  if (/CORN/.test(s)) return "CORN";
  if (/SOY/.test(s)) return "SOY";
  return s;
}
const jdKey = (clu, crop) => `${String(clu).toLowerCase()}|${plantFamily(crop)}`;

function actualPlanting(fields, jdFirst, cropYear, isFallSeededCrop) {
  const lo = `${cropYear}-03-01`, hi = `${cropYear}-07-31`;
  const ok = (v) => { const d = v == null ? "" : String(v).slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(d) && d >= lo && d <= hi ? d : null; };
  const crops = new Map();
  let acres = 0, datedAc = 0;
  for (const f of fields || []) {
    const code = String(f.rma_crop_code || "");
    if (!code || code === "0088") continue;                  // PRF has no planting
    if (isFallSeededCrop && isFallSeededCrop(f.crop)) continue; // planted last autumn
    const ac = num(f.acres) || 0;
    const rep = ok(f.report_date);
    const jd = !rep && jdFirst && f.clu_id != null ? ok(jdFirst.get(jdKey(f.clu_id, f.crop))) : null;
    const date = rep || jd;
    if (!crops.has(code)) crops.set(code, { code, crop: f.crop, acres: 0, dated_ac: 0, dates: [] });
    const c = crops.get(code);
    c.acres += ac; acres += ac;
    if (date) { c.dated_ac += ac; datedAc += ac; c.dates.push({ date, acres: ac, src: rep ? "report" : "jd" }); }
  }
  const list = [...crops.values()].map((c) => ({ ...c, dates: c.dates.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)) }))
    .sort((a, b) => b.acres - a.acres);
  const first = list.map((c) => c.dates[0] && c.dates[0].date).filter(Boolean).sort()[0] || null;
  return { crops: list, first, acres, dated_ac: datedAc };
}

// ── DAYS THAT MATTER FOR A CLAIM ─────────────────────────────────────────────
// Found from the county's own days and the crops' own RMA dates, never typed. Each
// is a span with what it was and why it was listed; the agent opens the printable
// record for it. Thresholds are stated on the page, and every one reads the county
// average of its cells, so a single wet or cold cell can sit inside a quieter day.
const CLAIM = Object.freeze({
  HEAVY_IN: 1.0,           // a county day of an inch or more
  WET_WINDOW_PCT: 125,     // a planting window at 125% of normal or more
  FROST_F: 32,             // a frost once the farm's own crop was planted
  HEAT_F: 90,              // a heat day in corn pollination
  POLLINATION: ["07-10", "08-15"],
  DRY_DAY_IN: 0.1,         // a day under a tenth is a dry day
  DRY_RUN_DAYS: 14,        // two weeks of them in the growing season
  DRY_SPAN: ["05-01", "09-30"],
  FROST_SPAN_END: "06-30",
  EARLY_FREEZE_DAYS: 7,    // a hard freeze a week or more before the county's median
});

// Consecutive days meeting `test`, as spans. A day that isn't there (null) ends a span.
function spans(days, test) {
  const out = [];
  let cur = null;
  for (const d of days) {
    if (test(d)) { if (!cur) cur = { from: d.date, to: d.date, days: [] }; cur.to = d.date; cur.days.push(d); }
    else if (cur) { out.push(cur); cur = null; }
  }
  if (cur) out.push(cur);
  return out;
}
const md = (iso) => iso.slice(5);
const inSpan = (iso, a, b) => md(iso) >= a && md(iso) <= b;

// days: countyDaily output, trimmed. plant: plantingDatesFor output. freezeMed: the
// county's median first 28°F day (a date) or null. actual: actualPlanting output, or
// null when the farm's planting dates are not known.
function claimDays(days, plant, freezeMed, actual) {
  const ev = [];
  const spring = (plant && plant.spring) || [];
  // 1. Wet planting windows, one per crop window that ran wet.
  for (const c of spring) {
    for (const w of c.windows) {
      const from = w.earliest_planting_date || (days[0] && days[0].date);
      if (!from || !w.final_planting_date) continue;
      const t = windowTotal(days, from, w.final_planting_date);
      if (t.pct != null && t.pct >= CLAIM.WET_WINDOW_PCT) {
        const wet = days.filter((d) => d.date >= from && d.date <= w.final_planting_date && d.counted && d.rain_in >= CLAIM.DRY_DAY_IN).length;
        ev.push({ kind: "wet_window", crop: c.crop, types: c.split ? w.types : null, from, to: w.final_planting_date,
                  rain_in: t.rain_in, normal_in: t.normal_in, pct: t.pct, wet_days: wet, gaps: t.missing });
      }
    }
  }
  // 2. Heavy rain days (each run of them is one event).
  for (const s of spans(days, (d) => d.counted && d.rain_in >= CLAIM.HEAVY_IN)) {
    ev.push({ kind: "heavy_rain", from: s.from, to: s.to, rain_in: Math.round(s.days.reduce((a, d) => a + d.rain_in, 0) * 100) / 100,
              max_in: Math.max(...s.days.map((d) => d.rain_in)), n: s.days.length });
  }
  // 3. Frost after planting, through June, from the farm's own planting dates: a
  // cold night before the first field went in is not a frost on a crop. RMA's earliest
  // planting date is never used here, and with no planting date on file none is listed.
  const plantStart = (actual && actual.first) || null;
  if (plantStart) {
    for (const s of spans(days, (d) => d.date >= plantStart && md(d.date) <= CLAIM.FROST_SPAN_END && d.tmin_f != null && d.tmin_f <= CLAIM.FROST_F)) {
      const crops = actual.crops.map((c) => {
        const by = c.dates.filter((x) => x.date <= s.to);
        return { crop: c.crop, planted_ac: Math.round(by.reduce((a, x) => a + x.acres, 0)), acres: Math.round(c.acres), fields: by.length };
      }).filter((c) => c.fields > 0);
      ev.push({ kind: "frost", from: s.from, to: s.to, min_f: Math.min(...s.days.map((d) => d.tmin_f)), n: s.days.length, after: plantStart, crops });
    }
  }
  // 4. Heat in corn pollination, only where the farm insures corn here.
  if (spring.some((c) => String(c.code) === "0041")) {
    for (const s of spans(days, (d) => inSpan(d.date, CLAIM.POLLINATION[0], CLAIM.POLLINATION[1]) && d.tmax_f != null && d.tmax_f >= CLAIM.HEAT_F)) {
      ev.push({ kind: "heat", from: s.from, to: s.to, max_f: Math.max(...s.days.map((d) => d.tmax_f)), n: s.days.length });
    }
  }
  // 5. Dry runs in the growing season. A day with no reading ends a run: it is not
  // known to be dry.
  for (const s of spans(days, (d) => inSpan(d.date, CLAIM.DRY_SPAN[0], CLAIM.DRY_SPAN[1]) && d.counted && d.rain_in < CLAIM.DRY_DAY_IN)) {
    if (s.days.length < CLAIM.DRY_RUN_DAYS) continue;
    ev.push({ kind: "dry_run", from: s.from, to: s.to, n: s.days.length,
              rain_in: Math.round(s.days.reduce((a, d) => a + d.rain_in, 0) * 100) / 100 });
  }
  // 6. An early hard freeze.
  const f = firstHardFreeze(days);
  if (f && freezeMed) {
    const early = Math.round((Date.parse(freezeMed + "T00:00:00Z") - Date.parse(f.date + "T00:00:00Z")) / 86400000);
    if (early >= CLAIM.EARLY_FREEZE_DAYS) ev.push({ kind: "early_freeze", from: f.date, to: f.date, min_f: f.tmin_f, days_early: early, median: freezeMed });
  }
  // 7. Days with no county reading, so nobody reads a gap as a quiet day.
  for (const s of spans(days, (d) => !d.counted)) ev.push({ kind: "missing", from: s.from, to: s.to, n: s.days.length });
  return ev.sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
}

module.exports = {
  MIN_CELL_SHARE, HARD_FREEZE_F, GDD_BASE_F, GDD_CAP_F, SEASON_START_MD, SEASON_END_MD,
  climoDoy, isoAdd, gddF, seasonBounds, countyDaily, trimTail, windowTotal, monthTotals,
  firstHardFreeze, medianFreezeDate, seasonSummary, clampRange, priorYear, plantingDatesFor, actualPlanting, plantFamily, jdKey,
  CLAIM, spans, claimDays,
};
